import React, {
  useState,
  useEffect,
  ChangeEvent,
  useRef,
  useCallback,
} from "react";
import "./AutoCompleteClient.css";
import { searchProductsByClient } from "../../services/clientService";
import StyledLabel from "../Label/StyledLabel";

export interface Product {
  id: number;
  description: string;
}

interface AutoCompleteProductProps {
  clientId: number | null;
  value: string;
  onChange: (value: string) => void;
  onSelect: (product: Product) => void;
  label?: string;
}

export function AutoCompleteProduct({
  clientId,
  value,
  onChange,
  onSelect,
  label,
}: AutoCompleteProductProps) {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (clientId) {
      setLoading(true);
      searchProductsByClient(clientId)
        .then((data: any) => {
          setAllProducts(data);
          if (open) setSuggestions(data);
        })
        .finally(() => setLoading(false));
    } else {
      setAllProducts([]);
      setSuggestions([]);
    }
  }, [clientId]);

  useEffect(() => {
    if (value) {
      const filtered = allProducts.filter((p) =>
        p.description.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filtered);
    }
  }, [value, allProducts]);

  const onClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    },
    [containerRef]
  );
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [onClickOutside]);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
  }
  function handleSelect(prod: Product) {
    onSelect(prod);
    setOpen(false);
    setSuggestions([]);
  }
  function handleFocus() {
    if (clientId !== null) {
      setSuggestions(allProducts);
      setOpen(true);
    }
  }

  return (
    <div className="form-group auto-complete-container" ref={containerRef}>
      <StyledLabel htmlFor="product-autocomplete">{label}</StyledLabel>
      <input
        autoComplete="off"
        id="product-autocomplete"
        className="form-control auto-complete-input"
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        aria-autocomplete="list"
        aria-controls="product-autocomplete-list"
        aria-expanded={open}
        disabled={!clientId}
      />

      {open && (
        <ul
          id="product-autocomplete-list"
          className="auto-complete-suggestions"
          role="listbox"
        >
          {loading && (
            <li className="auto-complete-loading" role="option">
              Carregando produtos…
            </li>
          )}
          {!loading && suggestions.length === 0 && (
            <li className="auto-complete-no-results" role="option">
              Nenhum produto encontrado
            </li>
          )}
          {!loading &&
            suggestions.map((p) => (
              <li
                key={p.id}
                className="auto-complete-suggestion"
                role="option"
                onClick={() => handleSelect(p)}
              >
                <span>{p.description}</span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
